import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout';
import { useToast } from '../hooks';
import {
  getPrompts,
  updatePrompt,
  getPromptHistory,
  revertPrompt,
} from '../api/systemPrompts';

// ─── Usage Context Badge Colors ──────────────────────────────────────────────
// Keys must match usage_context values seeded by seed_prompts.py
const CONTEXT_COLORS = {
  decision_engine: 'bg-primary-100 text-primary-700',
  external_feeds: 'bg-sky-100 text-sky-700',
  origination_worker: 'bg-emerald-100 text-emerald-700',
  regulatory_worker: 'bg-red-100 text-red-700',
  technology_worker: 'bg-amber-100 text-amber-700',
  distribution_worker: 'bg-cyan-100 text-cyan-700',
  proposal_orchestrator: 'bg-violet-100 text-violet-700',
  lead_research: 'bg-blue-100 text-blue-700',
  questionnaire: 'bg-primary-100 text-primary-700',
  questionnaire_update: 'bg-primary-100 text-primary-700',
  email_questionnaire_update: 'bg-primary-100 text-primary-700',
  transcript_consolidation: 'bg-teal-100 text-teal-700',
  transcript_summary: 'bg-teal-100 text-teal-700',
  meeting_notes: 'bg-orange-100 text-orange-700',
  email_generation: 'bg-pink-100 text-pink-700',
};

const getContextColor = (ctx) => CONTEXT_COLORS[ctx] || 'bg-gray-100 text-gray-700';

// ─── Icons ───────────────────────────────────────────────────────────────────
const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const HistoryIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CloseIcon = ({ size = 'w-6 h-6' }) => (
  <svg className={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ─── Main Page ───────────────────────────────────────────────────────────────
const SystemPromptsPage = () => {
  const { showToast } = useToast();

  // List state
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [contextFilter, setContextFilter] = useState('');

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({
    prompt_name: '',
    description: '',
    prompt_template: '',
    variables: '',
    usage_context: '',
    change_reason: '',
  });
  const [formSaving, setFormSaving] = useState(false);

  // History modal state
  const [historyPromptName, setHistoryPromptName] = useState(null);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Revert confirmation state
  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);

  // ─── Data Fetching ───────────────────────────────────────────────────────
  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (contextFilter) params.usage_context = contextFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await getPrompts(params);
      const fetched = res.data?.prompts || [];
      fetched.sort((a, b) => (a.prompt_name || '').localeCompare(b.prompt_name || ''));
      setPrompts(fetched);
    } catch {
      showToast('Failed to load prompts', 'error');
    } finally {
      setLoading(false);
    }
  }, [contextFilter, searchQuery, showToast]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // ─── Edit ───────────────────────────────────────────────────────────────
  const openEditModal = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      prompt_name: prompt.prompt_name,
      description: prompt.description || '',
      prompt_template: prompt.prompt_template || '',
      variables: (prompt.variables || []).join(', '),
      usage_context: prompt.usage_context || '',
      change_reason: '',
    });
    setShowFormModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async () => {
    setFormSaving(true);
    try {
      const variables = formData.variables
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);

      const payload = {
        prompt_template: formData.prompt_template,
        description: formData.description,
        variables,
        usage_context: formData.usage_context,
      };
      if (formData.change_reason) payload.change_reason = formData.change_reason;
      await updatePrompt(editingPrompt.prompt_name, payload);
      showToast('Prompt updated', 'success');

      setShowFormModal(false);
      fetchPrompts();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save prompt', 'error');
    } finally {
      setFormSaving(false);
    }
  };

  // ─── Version History ─────────────────────────────────────────────────────
  const openHistory = async (promptName) => {
    setHistoryPromptName(promptName);
    setHistoryVersions([]);
    setSelectedVersion(null);
    setHistoryLoading(true);
    try {
      const res = await getPromptHistory(promptName);
      const versions = res.data?.versions || [];
      setHistoryVersions(versions);
      if (versions.length > 0) setSelectedVersion(versions[0]);
    } catch {
      showToast('Failed to load version history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryPromptName(null);
    setHistoryVersions([]);
    setSelectedVersion(null);
  };

  // ─── Revert ──────────────────────────────────────────────────────────────
  const handleRevert = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      await revertPrompt(revertTarget.promptName, revertTarget.versionNumber);
      showToast(`Reverted to version ${revertTarget.versionNumber}`, 'success');
      setRevertTarget(null);
      closeHistory();
      fetchPrompts();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to revert', 'error');
    } finally {
      setReverting(false);
    }
  };

  // ─── Unique usage contexts from loaded prompts ───────────────────────────
  const usageContexts = [...new Set(prompts.map((p) => p.usage_context).filter(Boolean))].sort();

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
            System Prompts
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Manage AI worker prompts with version tracking
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative">
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-0 focus:border-primary transition-all"
            />
          </div>
          <select
            value={contextFilter}
            onChange={(e) => setContextFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary transition-all"
          >
            <option value="">All Contexts</option>
            {usageContexts.map((ctx) => (
              <option key={ctx} value={ctx}>
                {ctx.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Prompts Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : prompts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-sm">No prompts found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Context
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {prompts.map((prompt) => (
                  <tr key={prompt.prompt_name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono font-medium text-gray-900">
                        {prompt.prompt_name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {prompt.description}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getContextColor(
                          prompt.usage_context
                        )}`}
                      >
                        {prompt.usage_context?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm text-gray-700 font-mono">
                        v{prompt.version_number || 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {prompt.updated_at
                        ? new Date(prompt.updated_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(prompt)}
                          title="Edit prompt"
                          className="p-2 rounded-lg text-primary hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        >
                          <EditIcon />
                        </button>
                        <button
                          onClick={() => openHistory(prompt.prompt_name)}
                          title="Version history"
                          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <HistoryIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Edit Modal ───────────────────────────────────────────────────────── */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-strong max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Edit Prompt</h2>
                <p className="text-sm text-gray-500 font-mono mt-0.5">{formData.prompt_name}</p>
              </div>
              <button
                onClick={() => setShowFormModal(false)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-5">
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Brief description of the prompt's purpose"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary transition-all"
                  />
                </div>

                {/* Prompt Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Template
                  </label>
                  <textarea
                    value={formData.prompt_template}
                    onChange={(e) => handleFormChange('prompt_template', e.target.value)}
                    rows={14}
                    placeholder="Enter prompt template text..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed focus:ring-0 focus:border-primary transition-all"
                  />
                </div>

                {/* Two-column row */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Variables */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Variables{' '}
                      <span className="text-gray-400 font-normal">(comma-separated)</span>
                    </label>
                    <input
                      value={formData.variables}
                      onChange={(e) => handleFormChange('variables', e.target.value)}
                      placeholder="e.g. company_name, industry"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary transition-all"
                    />
                  </div>

                  {/* Usage Context */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usage Context
                    </label>
                    <input
                      value={formData.usage_context}
                      onChange={(e) => handleFormChange('usage_context', e.target.value)}
                      placeholder="e.g. origination, technology"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary transition-all"
                    />
                  </div>
                </div>

                {/* Change Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Change Reason{' '}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    value={formData.change_reason}
                    onChange={(e) => handleFormChange('change_reason', e.target.value)}
                    placeholder="Why are you making this change?"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowFormModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFormSubmit}
                disabled={formSaving}
                className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors shadow-md hover:shadow-lg"
              >
                {formSaving ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </span>
                ) : 'Update Prompt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Version History Modal ──────────────────────────────────────────── */}
      {historyPromptName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-strong max-w-5xl w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Version History</h2>
                <p className="text-sm text-gray-500 font-mono mt-0.5">{historyPromptName}</p>
              </div>
              <button
                onClick={closeHistory}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Body */}
            {historyLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
            ) : historyVersions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-500 text-sm">No version history yet.</p>
              </div>
            ) : (
              <div className="flex flex-1 min-h-0">
                {/* Left: version list */}
                <div className="w-72 border-r border-gray-200 overflow-y-auto flex-shrink-0">
                  {historyVersions.map((v) => (
                    <button
                      key={v.version_number}
                      onClick={() => setSelectedVersion(v)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                        selectedVersion?.version_number === v.version_number
                          ? 'bg-primary-50 border-l-2 border-l-primary'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${
                          selectedVersion?.version_number === v.version_number
                            ? 'text-primary'
                            : 'text-gray-900'
                        }`}>
                          Version {v.version_number}
                        </span>
                        <span className="text-xs text-gray-400">
                          {v.created_at
                            ? new Date(v.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })
                            : ''}
                        </span>
                      </div>
                      {v.created_by && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          by {v.created_by}
                        </p>
                      )}
                      {v.change_reason && (
                        <p className="text-xs text-gray-400 mt-1 truncate italic">
                          {v.change_reason}
                        </p>
                      )}
                    </button>
                  ))}
                </div>

                {/* Right: version detail */}
                <div className="flex-1 flex flex-col min-w-0">
                  {selectedVersion ? (
                    <>
                      <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-shrink-0">
                        <div>
                          <span className="text-sm font-semibold text-gray-700">
                            Version {selectedVersion.version_number}
                          </span>
                          <span className="text-xs text-gray-400 ml-3">
                            {selectedVersion.created_at
                              ? new Date(selectedVersion.created_at).toLocaleString()
                              : ''}
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            setRevertTarget({
                              promptName: historyPromptName,
                              versionNumber: selectedVersion.version_number,
                            })
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          Revert to this version
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-6">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono bg-gray-50 rounded-lg p-4 border border-gray-200 leading-relaxed">
                          {selectedVersion.prompt_template}
                        </pre>
                        {selectedVersion.description && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">
                              Description
                            </p>
                            <p className="text-sm text-gray-700">
                              {selectedVersion.description}
                            </p>
                          </div>
                        )}
                        {selectedVersion.variables?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">
                              Variables
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedVersion.variables.map((v) => (
                                <span
                                  key={v}
                                  className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-mono"
                                >
                                  {v}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center flex-1">
                      <p className="text-gray-400 text-sm">Select a version to view details</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Revert Confirmation Dialog ─────────────────────────────────────── */}
      {revertTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-strong max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-sans font-semibold text-gray-900 dark:text-white">Confirm Revert</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to revert{' '}
              <span className="font-mono font-semibold text-primary">{revertTarget.promptName}</span> to{' '}
              <span className="font-semibold">version {revertTarget.versionNumber}</span>.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This will create a new version with the content from version{' '}
              {revertTarget.versionNumber}. The current version will be preserved in history.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRevertTarget(null)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRevert}
                disabled={reverting}
                className="px-5 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors shadow-md"
              >
                {reverting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Reverting...
                  </span>
                ) : 'Revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SystemPromptsPage;
